import { FDRStatus, FieldModel, generatePath } from '@cibo/core'
import {
  ProgramOpportunityTeaser,
  useCreateProgramEngagement,
  useCreateWorkflow,
  usePrograms,
  useWorkflows,
} from '@cibo/programs'
import { Markdown, ResponsiveDialog, ResponsiveDialogProps } from '@cibo/ui'
import AccessTimeRounded from '@mui/icons-material/AccessTimeRounded'
import ChevronRight from '@mui/icons-material/ChevronRight'
import { LoadingButton } from '@mui/lab'
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { ENGAGEMENT_ROUTES } from '../../navigation/types/engagementRoutes'
import { ENROLLMENT_ROUTES, PROGRAM_ROUTES } from '../../navigation/types/routes'

interface FieldConfirmOpportunityDialogProps extends ResponsiveDialogProps {
  field?: FieldModel
  programId?: string
}

export const FieldConfirmOpportunityDialog = ({
  field,
  programId,
  ...dialogProps
}: FieldConfirmOpportunityDialogProps) => {
  const { t } = useTranslation('FieldConfirmOpportunityDialog')
  const { t: tTeaser } = useTranslation('@cibo/landmanager/FieldsTable')
  const programs = usePrograms()
  const program = programs.data?.find(p => p.programId === programId)
  const workflow = useWorkflows(0, 1, {
    programId,
    filters: !!field?.ownedBy.userId ? { userId: field?.ownedBy.userId } : undefined,
    benchmark: ['enrollment'],
  })
  const isLoading = workflow.isLoading || programs.isLoading
  const createWorkflow = useCreateWorkflow()
  const createEngagement = useCreateProgramEngagement()
  const navigate = useNavigate()

  const onCreateWorkflow = () => {
    if (field?.id && !!programId) {
      switch (program?.program.rulesVersion) {
        case 'v1':
          return createWorkflow
            .mutateAsync({
              name: '',
              userId: field.ownedBy.userId,
              fields: [field.id],
              benchmark: 'enrollment',
              doesUpsert: true,
              programId,
            })
            .then(response => {
              navigate(generatePath(ENROLLMENT_ROUTES.DETAIL, { workflowId: response.data.id }), {
                state: { isCreating: true },
              })
            })

        case 'v2':
          return createEngagement
            .mutateAsync({
              userId: field.ownedBy.userId,
              programId,
              fields: [field.id],
              doesUpsert: true,
            })
            .then(response => {
              const { id: engagementId } = response.data
              navigate(
                generatePath(`${PROGRAM_ROUTES.ENGAGEMENTS}/${ENGAGEMENT_ROUTES.ENGAGEMENT}`, {
                  engagementId,
                  programId,
                })
              )
            })
      }
    }
  }

  const isFuture = field?.fdrStatus
    .find(status => status.programId === programId)
    ?.status.includes(FDRStatus.futureEnrollment)

  return (
    <ResponsiveDialog {...dialogProps}>
      {isLoading ? (
        <DialogContent>
          <Stack direction="row" spacing={2}>
            <Skeleton variant="rounded" width={48} height={48} />
            <Stack spacing={1}>
              <Skeleton>
                <Typography>name</Typography>
              </Skeleton>
              <Skeleton>
                <Typography>name</Typography>
              </Skeleton>
            </Stack>
          </Stack>
        </DialogContent>
      ) : (
        <>
          {!!field && !!programId && (
            <>
              {!!isFuture && (
                <DialogTitle sx={{ backgroundColor: 'future.light', marginBottom: 2 }}>
                  <Stack direction="row" alignItems="center">
                    <AccessTimeRounded sx={{ marginRight: 1 }} />
                    {t('comingSoon')}
                  </Stack>
                </DialogTitle>
              )}
              <DialogContent>
                <Stack spacing={2}>
                  <ProgramOpportunityTeaser
                    field={field}
                    programId={programId}
                    size="lg"
                    t={tTeaser}
                  />
                  {!!isFuture && program?.program ? (
                    <>
                      <Markdown color="text.secondary">
                        {program?.program.content.fields.bulletedDescription}
                      </Markdown>
                      <Markdown color="text.secondary">
                        {program?.program.content.fields.longDescription}
                      </Markdown>
                    </>
                  ) : !field?.can(['field_enroll']) ? (
                    <Alert severity="error" data-testid="cannot-enroll">
                      {t('enrollmentPermissionMissing')}
                    </Alert>
                  ) : (
                    <>
                      {!!workflow.data?.numAvailable ? (
                        <Typography variant="body1">
                          <Trans
                            i18nKey={'FieldConfirmOpportunityDialog:existingEnrollment'}
                            values={{
                              fieldName: field.name,
                              programName: program?.program.content.fields.title,
                            }}
                            count={workflow.data?.items[0].fieldCounts.total}
                          >
                            <strong>fieldName</strong>
                            in
                            <strong>programName</strong>
                          </Trans>
                        </Typography>
                      ) : (
                        <Typography variant="body1">
                          <Trans
                            i18nKey={'FieldConfirmOpportunityDialog:createEnrollment'}
                            values={{
                              fieldName: field.name,
                              programName: program?.program.content.fields.title,
                            }}
                          >
                            <strong>fieldName</strong>
                            in
                            <strong>programName</strong>
                          </Trans>
                        </Typography>
                      )}
                      {!!program?.program && (
                        <>
                          <Divider />
                          <Markdown color="text.secondary">
                            {program?.program.content.fields.bulletedDescription}
                          </Markdown>
                          <Markdown color="text.secondary">
                            {program?.program.content.fields.longDescription}
                          </Markdown>
                        </>
                      )}
                      <Typography variant="caption">{t('rewardsDisclaimer')}</Typography>
                    </>
                  )}
                </Stack>
              </DialogContent>
              <DialogActions>
                {!!dialogProps?.onClose && (
                  //@ts-ignore
                  <Button onClick={e => dialogProps?.onClose(e, 'escapeKeyDown')}>
                    {!!isFuture || !field?.can(['field_enroll']) ? t('close') : t('cancel')}
                  </Button>
                )}
                {!isFuture && (
                  <LoadingButton
                    loading={createWorkflow.isLoading || createEngagement.isLoading}
                    onClick={onCreateWorkflow}
                    endIcon={<ChevronRight />}
                    variant="contained"
                    color="secondary"
                    disabled={!field?.can(['field_enroll'])}
                  >
                    {t('action', { context: !!workflow.data?.numAvailable ? 'update' : 'create' })}
                  </LoadingButton>
                )}
              </DialogActions>
            </>
          )}
        </>
      )}
    </ResponsiveDialog>
  )
}
